import * as THREE from 'three';
import { DSTEEL, DWOOD, STEEL, WOOD, cloth, emis, leather, metal, std } from '../materials.ts';
import { G, M, V, glowSprite, heaterShield, limb, ring, sword } from '../prims.ts';
import { figure } from './heroes.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';
import { tallerModel } from './taller.ts';
import { asterModel } from './aster.ts';
import { corazonModel } from './corazon.ts';
import { ojosModel } from './ojos.ts';
import { tormoModel } from './tormo.ts';
import { cazaModel } from './caza.ts';
import { caminoModel } from './camino.ts';
import { pasarelaModel } from './pasarela.ts';
import { nieveModel } from './nieve.ts';
import { ermitaModel } from './ermita.ts';
import { bosqueModel } from './bosque.ts';
import { otonoModel } from './otono.ts';
import { biblioModel } from './biblio.ts';

/**
 * The paths' own people (docs/sendas/*.md), painted miniatures like the sagas' (models/saga.ts), rigged with the
 * figure's bones so the folk's clips move them (walk, dig, sit, kneel). The warrior's: Wenn, eighty, a weaver from
 * Musgoviejo, small and straight-backed in dark wool with her shawl and stick; Kel, thirty, a woodcutter gone recruit,
 * broad, the garrison's tabard over a quilted jacket, the stretcher with the Company's shields on his back and a short
 * spade in his hand. And the Company's shield (a heater of pale stone-grey wood with a red cloth cross tied on), which
 * Kel digs out of the snow, carries and plants.
 *
 * Colors: cold charcoal and slate, pale skin, the red of the Company's cloth (#b8322a), no gold.
 */

export const COMPANY_RED = '#b8322a';

/** The Company's shield, upright, its face to +Z: weathered pale wood, an iron rim, a red cloth cross tied over it. */
export const companyShield = (parent: THREE.Object3D, scale = 1): THREE.Group => {
  const g = G(parent);
  const wood = std('#8e8a84', { roughness: 0.95, flatShading: true });
  const face = heaterShield(0.42 * scale, 0.56 * scale, wood);
  face.position.z = -0.02;
  g.add(face);
  // The rim, darker iron.
  const rim = heaterShield(0.46 * scale, 0.6 * scale, DSTEEL);
  rim.position.z = -0.035;
  rim.scale.z = 0.6;
  g.add(rim);
  // The cross of red cloth, tied on: two strips, the knot at the middle.
  const red = cloth(COMPANY_RED);
  M(new THREE.BoxGeometry(0.06 * scale, 0.5 * scale, 0.012), red, g).position.set(0, 0.0, 0.03);
  M(new THREE.BoxGeometry(0.36 * scale, 0.06 * scale, 0.012), red, g).position.set(0, 0.08 * scale, 0.032);
  M(new THREE.SphereGeometry(0.035 * scale, 6, 5), red, g).position.set(0, 0.08 * scale, 0.045);
  return g;
};

/** Wenn: small, upright for her eighty years, dark wool, the slate shawl, a grey braid, her stick in the right hand. */
const wenn = (o?: BaseOpts): Model => {
  const c = begin(o, 0.58, 811);
  const wool = cloth('#2c323c');
  const skin = std('#d2b4a2', { roughness: 0.85 });
  const f = figure({ legs: wool, boots: leather('#221c18'), torso: wool, arms: wool, hands: skin, skin });
  c.g.add(f.fig);
  // Smaller than the heroes: an old woman.
  f.fig.scale.setScalar(0.9);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The long skirt to the ankles, and an apron of undyed wool.
  const skirt = M(new THREE.CylinderGeometry(0.19, 0.31, 0.84, 12, 1, true), std('#262b33', { roughness: 1, side: THREE.DoubleSide }), b);
  skirt.position.y = 0.48;
  M(new THREE.BoxGeometry(0.24, 0.5, 0.02), cloth('#8b8780'), b).position.set(0, 0.62, 0.21);
  // The shawl, heavy, crossed over the chest and tied behind.
  const shawl = std('#4a5462', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.16, 0.31, 0.34, 12, 1, true), shawl, b).position.y = 1.32;
  for (const k of [-1, 1]) {
    const flap = M(new THREE.BoxGeometry(0.13, 0.32, 0.02), shawl, b);
    flap.position.set(k * 0.06, 1.17, 0.2);
    flap.rotation.z = k * 0.42;
  }
  M(new THREE.ConeGeometry(0.24, 0.42, 3, 1, true), shawl, b).position.set(0, 1.1, -0.2);
  // A knitting bag at the hip, two needles out of it.
  const bag = M(new THREE.SphereGeometry(0.07, 8, 6), cloth('#5a4a3e'), b);
  bag.position.set(-0.2, 0.86, 0.06);
  bag.scale.set(1, 1.2, 0.8);
  for (const k of [-1, 1]) limb(b, V(-0.2 + k * 0.012, 0.9, 0.06), V(-0.19 + k * 0.03, 1.02, 0.08), 0.006, 0.005, std('#9aa0a8', { roughness: 0.4 }), 4);
  // Her head: a dark wool kerchief tied under the chin, white hair showing at the brow in two swept bands, the braid
  // down her back; a thin face, a long nose, deep lines by the mouth. (Read as a bald grey head in the portraits when
  // her hair was a smooth cap the colour of her skin.)
  const white = std('#e6e3de', { roughness: 1 });
  const scarf = std('#3b3440', { roughness: 1, side: THREE.DoubleSide });
  const kerchief = M(new THREE.SphereGeometry(0.142, 14, 10, 0, Math.PI * 2, 0, Math.PI * 0.56), scarf, h);
  kerchief.position.set(0, 1.645, -0.022);
  kerchief.rotation.x = -0.28;
  // Its knot under the chin and the fold over the brow.
  M(new THREE.SphereGeometry(0.03, 7, 5), scarf, h).position.set(0, 1.525, 0.07);
  ring(h, 0.136, 0.016, std('#2c2731', { roughness: 1 }), 1.7, 4, 16).rotation.x = -0.32;
  for (const k of [-1, 1]) {
    const band = M(new THREE.SphereGeometry(0.06, 8, 6), white, h);
    band.position.set(k * 0.058, 1.712, 0.083);
    band.scale.set(1.15, 0.42, 0.55);
    band.rotation.z = k * 0.45;
    // The kerchief's sides down to the jaw.
    const side = M(new THREE.BoxGeometry(0.02, 0.15, 0.12), scarf, h);
    side.position.set(k * 0.126, 1.6, 0.0);
    side.rotation.z = k * 0.12;
  }
  for (let i = 0; i < 4; i++) {
    const bead = M(new THREE.SphereGeometry(0.034 - i * 0.004, 6, 5), white, h);
    bead.position.set(0, 1.56 - i * 0.07, -0.14 - i * 0.012);
  }
  const lined = std('#9c7e6c', { roughness: 0.9 });
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.042, 1.652, 0.114);
    // Brows white and thin; the lines from the nose to the mouth's corners; crow's feet.
    M(new THREE.BoxGeometry(0.042, 0.009, 0.014), white, h).position.set(k * 0.043, 1.675, 0.11);
    const fold = M(new THREE.BoxGeometry(0.006, 0.05, 0.01), lined, h);
    fold.position.set(k * 0.036, 1.595, 0.116);
    fold.rotation.z = k * -0.35;
    M(new THREE.BoxGeometry(0.018, 0.004, 0.008), lined, h).position.set(k * 0.072, 1.652, 0.1);
  }
  const nose = M(new THREE.ConeGeometry(0.018, 0.055, 5), std('#c8a490', { roughness: 0.85 }), h);
  nose.position.set(0, 1.626, 0.132);
  nose.rotation.x = Math.PI / 2 + 0.25;
  M(new THREE.BoxGeometry(0.045, 0.006, 0.01), std('#7a5450'), h).position.set(0, 1.578, 0.111);
  // The stick: a dark knotted staff in the right hand, to the ground.
  const stick = G(f.rhAbs, 0.28, 0.9, 0.14);
  limb(stick, V(0, -0.82, 0.02), V(0, 0.14, 0), 0.016, 0.02, DWOOD, 5);
  M(new THREE.SphereGeometry(0.028, 6, 5), DWOOD, stick).position.y = 0.15;
  f.rig.head.rotation.x = 0.04;
  return done(c, 1.6, () => undefined, f.rig);
};

/** Kel: broad and slow, a quilted jacket under the garrison's tabard, the stretcher of shields on his back, a spade. */
const kel = (o?: BaseOpts): Model => {
  const c = begin(o, 0.64, 812);
  const quilt = cloth('#4b4a46');
  const skin = std('#c49a80', { roughness: 0.8 });
  const f = figure({ legs: cloth('#3a3c40'), boots: leather('#2a201a'), torso: quilt, shoulders: quilt, arms: quilt, forearms: leather('#3e3026'), hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(1.08, 1.04, 1.08);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The garrison's tabard, new and too clean.
  M(new THREE.BoxGeometry(0.26, 0.52, 0.016), cloth('#2c3a58'), b).position.set(0, 1.08, 0.18);
  M(new THREE.BoxGeometry(0.26, 0.4, 0.016), cloth('#2c3a58'), b).position.set(0, 1.14, -0.18);
  // Quilting lines across the jacket.
  for (let i = 0; i < 3; i++) ring(b, 0.205 - i * 0.012, 0.008, cloth('#3e3d3a'), 1.08 + i * 0.12, 4, 14);
  // The stretcher on his back: two long poles, cross slats, and the shields tied on with rope.
  const frame = G(b, 0, 1.12, -0.26);
  frame.rotation.x = -0.12;
  for (const k of [-1, 1]) limb(frame, V(k * 0.17, -0.5, 0), V(k * 0.17, 0.62, 0), 0.02, 0.02, WOOD, 5);
  for (const y of [-0.38, 0.0, 0.42]) M(new THREE.BoxGeometry(0.4, 0.035, 0.03), WOOD, frame).position.set(0, y, 0);
  const sh = companyShield(frame, 0.82);
  sh.position.set(0, 0.12, -0.06);
  sh.rotation.y = Math.PI;
  const rope = std('#6b5a3e', { roughness: 1 });
  for (const y of [0.28, -0.12]) M(new THREE.BoxGeometry(0.42, 0.018, 0.09), rope, frame).position.set(0, y, -0.03);
  // Straps over the shoulders.
  for (const k of [-1, 1]) {
    const strap = M(new THREE.BoxGeometry(0.045, 0.5, 0.02), leather('#3a2a1c'), b);
    strap.position.set(k * 0.12, 1.24, 0.17);
    strap.rotation.z = k * -0.12;
  }
  // Short dark hair, a short beard, a broad face.
  const hair = std('#2e2621', { roughness: 1 });
  M(new THREE.SphereGeometry(0.128, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.42), hair, h).position.set(0, 1.648, -0.005);
  const beard = M(new THREE.SphereGeometry(0.1, 10, 7, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5), hair, h);
  beard.position.set(0, 1.6, 0.03);
  beard.scale.set(1.0, 0.9, 0.9);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.656, 0.115);
    M(new THREE.BoxGeometry(0.05, 0.014, 0.02), hair, h).position.set(k * 0.045, 1.684, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.05, 5), std('#b4866c', { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.133);
  nose.rotation.x = Math.PI / 2;
  // A short spade in the right hand (to dig the shields out).
  const spade = G(f.rhAbs, 0.28, 0.9, 0.14);
  limb(spade, V(0, -0.1, 0), V(0, 0.52, 0), 0.016, 0.016, WOOD, 5);
  M(new THREE.BoxGeometry(0.12, 0.16, 0.012), DSTEEL, spade).position.y = 0.62;
  M(new THREE.BoxGeometry(0.1, 0.025, 0.025), WOOD, spade).position.y = -0.11;
  f.rig.head.rotation.x = 0.06;
  return done(c, 1.82, () => undefined, f.rig);
};

/** Wenn on her mule, waiting: the mule standing, head low, a blanket and the saddle; Wenn seated sideways, the stick across. */
const wennMule = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 813);
  const mule = muleBody(c.g, '#4a3f38');
  // Blanket and saddle.
  M(new THREE.BoxGeometry(0.5, 0.04, 0.5), cloth('#3c4a5a'), mule).position.set(0, 0.99, 0);
  M(new THREE.BoxGeometry(0.34, 0.08, 0.36), leather('#3a2a1c'), mule).position.set(0, 1.04, 0);
  // Saddlebags, one with a skein of wool hanging out.
  for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.06, 0.2, 0.22), leather('#4a3424'), mule).position.set(k * 0.24, 0.86, -0.18);
  // Wenn, seated sideways on it (her figure posed still).
  const w = wenn();
  const rider = w.root;
  rider.position.set(0.04, 0.32, 0.02);
  rider.rotation.y = Math.PI / 2;
  const rig = w.rig;
  if (rig) {
    for (const leg of [rig.legL, rig.legR]) leg.rotation.x = -1.5;
    for (const shin of [rig.shinL, rig.shinR]) if (shin) shin.rotation.x = 1.45;
    rig.armL.rotation.set(-0.5, 0, 0.2);
    rig.armR.rotation.set(-0.3, 0, -0.25);
    rig.head.rotation.y = -1.2;
  }
  c.g.add(rider);
  return done(c, 1.9, () => undefined);
};

/** A mule's body (head, mane, tail, legs) in a coat colour, standing, under `parent`. */
export const muleBody = (parent: THREE.Object3D, coatColor: string): THREE.Group => {
  const coat = std(coatColor, { roughness: 0.95, flatShading: true });
  const dark = std('#2e2724', { roughness: 0.95, flatShading: true });
  const mule = G(parent, 0, 0, 0);
  // Body, belly to rump, neck and the long head down a little.
  const body = M(new THREE.CylinderGeometry(0.2, 0.22, 0.86, 10), coat, mule);
  body.rotation.x = Math.PI / 2;
  body.position.set(0, 0.78, 0);
  M(new THREE.SphereGeometry(0.21, 10, 8), coat, mule).position.set(0, 0.8, -0.42);
  M(new THREE.SphereGeometry(0.2, 10, 8), coat, mule).position.set(0, 0.8, 0.4);
  limb(mule, V(0, 0.86, 0.48), V(0, 1.12, 0.72), 0.11, 0.09, coat, 7);
  const head = G(mule, 0, 1.08, 0.84);
  head.rotation.x = 0.7;
  M(new THREE.BoxGeometry(0.14, 0.14, 0.34), coat, head).position.set(0, 0, 0.1);
  M(new THREE.BoxGeometry(0.12, 0.1, 0.12), std('#8a7e74', { roughness: 0.9 }), head).position.set(0, -0.02, 0.27);
  for (const k of [-1, 1]) {
    const ear = M(new THREE.ConeGeometry(0.035, 0.24, 5), coat, head);
    ear.position.set(k * 0.05, 0.14, -0.04);
    ear.rotation.z = -k * 0.25;
    M(new THREE.SphereGeometry(0.014, 5, 4), std('#141214'), head).position.set(k * 0.07, 0.03, 0.12);
  }
  // A dark mane, the tail.
  M(new THREE.BoxGeometry(0.04, 0.12, 0.34), dark, mule).position.set(0, 1.1, 0.6);
  limb(mule, V(0, 0.82, -0.6), V(0, 0.42, -0.7), 0.03, 0.06, dark, 5);
  // Legs, the hooves.
  for (const [x, z] of [[-0.12, 0.34], [0.12, 0.34], [-0.12, -0.34], [0.12, -0.34]] as const) {
    limb(mule, V(x, 0.72, z), V(x, 0.06, z), 0.05, 0.038, coat, 6);
    M(new THREE.CylinderGeometry(0.04, 0.045, 0.06, 7), std('#1a1614'), mule).position.set(x, 0.03, z);
  }
  return mule;
};

/**
 * The high farms' people (chapter 2's close): Dalmo, forty-odd, Sanne's grandson, broad and weathered, a sheepskin
 * coat open over a wool smock, a felt hat with a wide brim, a crook in his hand, the farms' grey and brown, no red;
 * two farmers in hoods and wool, one leading a pack mule (`farmer2`).
 */
const farmFolk = (o: BaseOpts | undefined, seed: number, look: { coat: string; under: string; skin: string; hair: string; hat: 'brim' | 'hood' | 'cap'; build: number; beard: boolean; crook?: boolean; sack?: boolean }): Model => {
  const c = begin(o, 0.64, seed);
  const coat = cloth(look.coat);
  const skin = std(look.skin, { roughness: 0.85 });
  const f = figure({ legs: cloth('#3a3530'), boots: leather('#2a201a'), torso: cloth(look.under), shoulders: coat, arms: coat, forearms: coat, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(look.build, 1.02, look.build);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The coat: long, open at the front, its fleece showing at the collar and the hem.
  const long = M(new THREE.CylinderGeometry(0.2, 0.3, 0.72, 12, 1, true, 0.5, Math.PI * 2 - 1.0), std(look.coat, { roughness: 1, side: THREE.DoubleSide }), b);
  long.position.y = 0.86;
  const fleece = std('#cfc6b6', { roughness: 1 });
  ring(b, 0.15, 0.04, fleece, 1.44, 4, 12);
  ring(b, 0.29, 0.03, fleece, 0.51, 4, 14);
  M(new THREE.BoxGeometry(0.42, 0.05, 0.3), leather('#3a2a1c'), b).position.set(0, 0.95, 0.0);
  // Hair, beard, the face's few marks.
  const hair = std(look.hair, { roughness: 1 });
  M(new THREE.SphereGeometry(0.127, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.45), hair, h).position.set(0, 1.648, -0.01);
  if (look.beard) {
    const beard = M(new THREE.SphereGeometry(0.1, 10, 7, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5), hair, h);
    beard.position.set(0, 1.6, 0.035);
    beard.scale.set(1.0, 0.85, 0.9);
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.05, 0.013, 0.02), hair, h).position.set(k * 0.045, 1.684, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.05, 5), std(look.skin, { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.133);
  nose.rotation.x = Math.PI / 2;
  if (look.hat === 'brim') {
    // A felt hat, wide-brimmed and battered, pulled low against the wind.
    const felt = std('#3b3631', { roughness: 1 });
    M(new THREE.CylinderGeometry(0.25, 0.25, 0.02, 16), felt, h).position.set(0, 1.74, 0);
    M(new THREE.CylinderGeometry(0.11, 0.13, 0.12, 12), felt, h).position.set(0, 1.8, -0.01);
    M(new THREE.CylinderGeometry(0.132, 0.132, 0.025, 12), std('#5a4a3e', { roughness: 1 }), h).position.set(0, 1.76, -0.01);
  } else if (look.hat === 'hood') {
    const hood = std(look.coat, { roughness: 1, side: THREE.DoubleSide });
    const hd = M(new THREE.SphereGeometry(0.16, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.62), hood, h);
    hd.position.set(0, 1.65, -0.03);
    hd.rotation.x = -0.3;
  } else {
    M(new THREE.SphereGeometry(0.13, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.4), std('#5a5048', { roughness: 1 }), h).position.set(0, 1.69, -0.01);
  }
  if (look.crook) {
    // A shepherd's crook, taller than he is, in the right hand.
    const crook = G(f.rhAbs, 0.28, 0.9, 0.14);
    limb(crook, V(0, -0.86, 0.02), V(0, 0.55, 0), 0.016, 0.018, DWOOD, 5);
    const top = M(new THREE.TorusGeometry(0.07, 0.014, 4, 10, Math.PI * 1.2), DWOOD, crook);
    top.position.set(0.06, 0.58, 0);
  }
  if (look.sack) {
    // A sack over the shoulder, a coil of rope at the hip.
    const sack = M(new THREE.SphereGeometry(0.15, 9, 7), cloth('#8a7e6c'), b);
    sack.position.set(0.12, 1.3, -0.2);
    sack.scale.set(0.9, 1.2, 0.8);
    M(new THREE.TorusGeometry(0.08, 0.02, 4, 10), std('#7a6a52', { roughness: 1 }), b).position.set(-0.22, 0.85, 0.04);
  }
  f.rig.head.rotation.x = 0.05;
  return done(c, 1.82, () => undefined, f.rig);
};

const dalmo = (o?: BaseOpts): Model => farmFolk(o, 815, { coat: '#5a4a3c', under: '#4a4842', skin: '#c08c70', hair: '#3a2e26', hat: 'brim', build: 1.12, beard: true, crook: true });
const farmer = (o?: BaseOpts): Model => farmFolk(o, 816, { coat: '#4e4a44', under: '#5c5246', skin: '#caa088', hair: '#5a4a3a', hat: 'hood', build: 1.0, beard: true, sack: true });

/** The second farmer, leading the farms' pack mule (one figure: the man at its head). */
const farmer2 = (o?: BaseOpts): Model => {
  const m = farmFolk(o, 817, { coat: '#3e4248', under: '#5a5650', skin: '#b88a6e', hair: '#2a2420', hat: 'cap', build: 0.98, beard: false });
  const mule = muleBody(m.root, '#5a4c40');
  mule.position.set(0.55, 0, -0.75);
  mule.rotation.y = 0.15;
  // Packs on it: two baskets and a rolled hide, a lead rope to his hand.
  for (const k of [-1, 1]) M(new THREE.CylinderGeometry(0.13, 0.11, 0.26, 8), std('#7a6448', { roughness: 1 }), mule).position.set(k * 0.27, 0.9, 0);
  const roll = M(new THREE.CylinderGeometry(0.09, 0.09, 0.5, 8), std('#8a7e6c', { roughness: 1 }), mule);
  roll.rotation.z = Math.PI / 2;
  roll.position.set(0, 1.06, -0.05);
  return m;
};

/** The Company's shield standing in the snow as Kel finds it (or planted in a wall's gap). */
const shieldUp = (o?: BaseOpts): Model => {
  const c = begin(o, 0.3, 814);
  const s = companyShield(c.g, 1.15);
  s.position.y = 0.32;
  s.rotation.x = -0.12;
  return done(c, 0.75, () => undefined);
};

/**
 * The Company of the Pass, sixty years back (chapter 3's memory): guardians of the Order in their winter gear, the
 * Order's grey under a quilted coat, a steel cap or a hood, each with the Company's shield (the red cloth cross tied on).
 * `who` sets the face and the trade: Ruy the captain (grey beard, a sword, the shield on his arm: he is played, so he
 * holds them as the warrior does), Fermín (lean, hooded, a long boathook to work the roof), Juana (a bow), Sanne
 * (broad, a braid, an axe), Tomé (young and big, a spear), Harl (nineteen, tall, the banner).
 */
interface CompanyLook {
  coat: string;
  skin: string;
  hair: string;
  build: number;
  tall: number;
  beard?: 'grey' | 'short' | 'none';
  hood?: boolean;
  braid?: boolean;
  cap?: boolean;
  hand: 'sword' | 'hook' | 'bow' | 'axe' | 'spear' | 'banner';
}

const companyFolk = (o: BaseOpts | undefined, seed: number, L: CompanyLook): Model => {
  const c = begin(o, 0.64, seed);
  const coat = cloth(L.coat);
  const skin = std(L.skin, { roughness: 0.8 });
  const mail = metal('#7d838c', { roughness: 0.55, flatShading: true });
  const f = figure({ legs: cloth('#34363b'), boots: leather('#231c17'), torso: coat, shoulders: mail, arms: coat, forearms: leather('#3a2e26'), hands: leather('#2e2520'), skin, cape: '#4b5058', capeLen: 1.0 });
  c.g.add(f.fig);
  f.fig.scale.set(L.build, L.tall, L.build);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The Order's grey surcoat over the quilt, a mail collar, a wide belt.
  M(new THREE.BoxGeometry(0.27, 0.56, 0.016), cloth('#5c6068'), b).position.set(0, 1.07, 0.18);
  M(new THREE.BoxGeometry(0.27, 0.44, 0.016), cloth('#5c6068'), b).position.set(0, 1.12, -0.18);
  ring(b, 0.15, 0.035, mail, 1.46, 5, 14);
  ring(b, 0.19, 0.03, leather('#2a1f18'), 0.99, 5, 14);
  const hair = std(L.hair, { roughness: 1 });
  if (L.hood) {
    const hood = M(new THREE.SphereGeometry(0.155, 12, 9, 0, Math.PI * 2, 0, Math.PI * 0.6), std('#454a52', { roughness: 1, side: THREE.DoubleSide }), h);
    hood.position.set(0, 1.655, -0.03);
    hood.rotation.x = -0.32;
  } else if (L.cap) {
    const cap = M(new THREE.SphereGeometry(0.138, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.45), STEEL, h);
    cap.position.set(0, 1.66, -0.005);
    M(new THREE.BoxGeometry(0.028, 0.09, 0.02), STEEL, h).position.set(0, 1.64, 0.13);
  } else {
    M(new THREE.SphereGeometry(0.13, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.46), hair, h).position.set(0, 1.652, -0.012);
  }
  if (L.braid) for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.034 - i * 0.004, 6, 5), hair, h).position.set(0, 1.58 - i * 0.07, -0.135 - i * 0.012);
  if (L.beard && L.beard !== 'none') {
    const beard = M(new THREE.SphereGeometry(L.beard === 'grey' ? 0.11 : 0.098, 10, 7, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5), std(L.beard === 'grey' ? '#b9b6b0' : L.hair, { roughness: 1 }), h);
    beard.position.set(0, L.beard === 'grey' ? 1.59 : 1.6, 0.035);
    beard.scale.set(1.0, L.beard === 'grey' ? 1.1 : 0.85, 0.9);
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.05, 0.013, 0.02), L.beard === 'grey' ? std('#b9b6b0') : hair, h).position.set(k * 0.045, 1.684, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.02, 0.05, 5), std(L.skin, { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.133);
  nose.rotation.x = Math.PI / 2;
  // The Company's shield: on the arm for those who fight with one hand, slung on the back for the others.
  if (L.hand === 'sword' || L.hand === 'axe') {
    const sh = companyShield(f.lh, 0.95);
    sh.position.set(-0.06, 0.06, 0.04);
    sh.rotation.y = -0.35;
  } else {
    const back = G(b, 0, 1.2, -0.24);
    const sh = companyShield(back, 0.85);
    sh.rotation.y = Math.PI;
    sh.rotation.z = 0.2;
  }
  if (L.hand === 'sword') {
    const sw = sword(0.82, STEEL, DSTEEL);
    f.rh.add(sw);
    sw.rotation.set(0.35, 0, -0.12);
  } else if (L.hand === 'axe') {
    const axe = G(f.rh);
    axe.rotation.set(0.3, 0, -0.1);
    limb(axe, V(0, -0.05, 0), V(0, 0.62, 0), 0.016, 0.018, WOOD, 5);
    M(new THREE.BoxGeometry(0.03, 0.16, 0.13), DSTEEL, axe).position.set(0, 0.56, 0.06);
  } else if (L.hand === 'spear' || L.hand === 'hook') {
    const pole = G(f.rh);
    pole.rotation.set(0.15, 0, 0);
    limb(pole, V(0, -0.9, 0), V(0, 0.9, 0), 0.016, 0.016, DWOOD, 5);
    if (L.hand === 'spear') M(new THREE.ConeGeometry(0.03, 0.2, 4), STEEL, pole).position.y = 1.0;
    else {
      // The roofer's boathook: a spike and a hook at the end.
      M(new THREE.ConeGeometry(0.018, 0.12, 4), DSTEEL, pole).position.y = 0.96;
      const hk = M(new THREE.TorusGeometry(0.05, 0.012, 4, 8, Math.PI), DSTEEL, pole);
      hk.position.set(0.05, 0.88, 0);
    }
  } else if (L.hand === 'bow') {
    const bow = G(f.lh);
    const arc = M(new THREE.TorusGeometry(0.42, 0.014, 4, 18, Math.PI * 0.9), DWOOD, bow);
    arc.rotation.set(0, Math.PI / 2, Math.PI / 2 + Math.PI * 0.05);
    // A quiver at the hip.
    const q = M(new THREE.CylinderGeometry(0.045, 0.035, 0.42, 7), leather('#3a2a1c'), b);
    q.position.set(0.18, 1.05, -0.16);
    q.rotation.z = -0.3;
  } else if (L.hand === 'banner') {
    // The banner: a tall staff, the Order's grey cloth with the Company's red cross.
    const st = G(f.rh);
    st.rotation.set(0.05, 0, 0);
    limb(st, V(0, -0.85, 0), V(0, 1.55, 0), 0.016, 0.018, DWOOD, 5);
    M(new THREE.BoxGeometry(0.42, 0.02, 0.02), DWOOD, st).position.set(0.2, 1.5, 0);
    const flag = M(new THREE.BoxGeometry(0.4, 0.6, 0.012), cloth('#6b7078'), st);
    flag.position.set(0.21, 1.18, 0);
    const red = cloth(COMPANY_RED);
    M(new THREE.BoxGeometry(0.07, 0.56, 0.016), red, st).position.set(0.21, 1.18, 0);
    M(new THREE.BoxGeometry(0.36, 0.07, 0.016), red, st).position.set(0.21, 1.28, 0);
  }
  f.rig.head.rotation.x = 0.04;
  return done(c, 1.82 * L.tall, () => undefined, f.rig);
};

const ruy = (o?: BaseOpts): Model => companyFolk(o, 821, { coat: '#3f4450', skin: '#c49a82', hair: '#6a6660', build: 1.06, tall: 1.02, beard: 'grey', cap: true, hand: 'sword' });
const fermin = (o?: BaseOpts): Model => companyFolk(o, 822, { coat: '#4a4a44', skin: '#b88e72', hair: '#2b2420', build: 0.92, tall: 1.0, beard: 'short', hood: true, hand: 'hook' });
const juana = (o?: BaseOpts): Model => companyFolk(o, 823, { coat: '#454c46', skin: '#c9a088', hair: '#3a2a20', build: 0.9, tall: 0.97, hood: true, braid: true, hand: 'bow' });
const sanne = (o?: BaseOpts): Model => companyFolk(o, 824, { coat: '#4e4640', skin: '#c69a7e', hair: '#7a5a3c', build: 1.04, tall: 0.98, braid: true, hand: 'axe' });
const tome = (o?: BaseOpts): Model => companyFolk(o, 825, { coat: '#403f48', skin: '#b98b6c', hair: '#211b17', build: 1.16, tall: 1.05, beard: 'short', cap: true, hand: 'spear' });
const harl = (o?: BaseOpts): Model => companyFolk(o, 826, { coat: '#3e4552', skin: '#d0a68a', hair: '#5a4632', build: 0.98, tall: 1.06, hand: 'banner' });

/**
 * Chapter 4's people. Dalmo's mule with the stretcher roped across its back, the nine shields stacked on it, their red
 * crosses showing (`muleLoad`). Old Harl (`harlOld`): seventy-nine, tall still but bent, white hair and beard gone
 * wild, a goatherd's patched coat of grey wool and hide, a crook, and the Company's banner folded small and tied on his
 * back, the red of its cross showing at the fold. Fermín and Juana in the ice (`frozenPair`): sitting back to back
 * against each other, frost over them, their shields across their knees, rime on the hoods; the figures in the
 * frozen blue of the cave.
 */
const muleLoad = (o?: BaseOpts): Model => {
  const c = begin(o, 0.8, 831);
  const mule = muleBody(c.g, '#4f4339');
  M(new THREE.BoxGeometry(0.46, 0.04, 0.56), cloth('#3a4450'), mule).position.set(0, 0.99, 0);
  // The stretcher's poles across the pack saddle, the shields stacked on it in three rows, rope over them.
  for (const k of [-1, 1]) {
    const pole = M(new THREE.CylinderGeometry(0.022, 0.022, 1.3, 6), DWOOD, mule);
    pole.rotation.x = Math.PI / 2;
    pole.position.set(k * 0.2, 1.06, 0);
  }
  const wood = std('#8e8a84', { roughness: 0.95, flatShading: true });
  const red = cloth(COMPANY_RED);
  for (let i = 0; i < 9; i++) {
    const row = Math.floor(i / 3);
    const sh = G(mule, ((i % 3) - 1) * 0.14, 1.1 + row * 0.05, -0.36 + row * 0.36);
    sh.rotation.set(-1.35, 0, ((i % 3) - 1) * 0.15);
    M(new THREE.BoxGeometry(0.15, 0.22, 0.02), wood, sh);
    M(new THREE.BoxGeometry(0.035, 0.2, 0.012), red, sh).position.z = 0.014;
    M(new THREE.BoxGeometry(0.12, 0.035, 0.012), red, sh).position.set(0, 0.04, 0.014);
  }
  const rope = std('#7a6a52', { roughness: 1 });
  for (const z of [-0.25, 0.25]) M(new THREE.TorusGeometry(0.24, 0.012, 4, 12, Math.PI), rope, mule).position.set(0, 1.1, z);
  return done(c, 1.6, () => undefined);
};

const harlOld = (o?: BaseOpts, banner = false): Model => {
  const c = begin(o, 0.64, 832);
  const coat = cloth('#565a5e');
  const skin = std('#c99c86', { roughness: 0.85 });
  const f = figure({ legs: cloth('#3a3734'), boots: leather('#231c17'), torso: coat, shoulders: cloth('#6b645a'), arms: coat, forearms: leather('#4a3e34'), hands: skin, skin, cape: '#4a4e54', capeLen: 0.8 });
  c.g.add(f.fig);
  f.fig.scale.set(0.98, 1.04, 0.98);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // Bent: the head forward, a hide over the shoulders, patches on the coat.
  const hide = std('#8a7a66', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.17, 0.27, 0.26, 12, 1, true), hide, b).position.y = 1.36;
  M(new THREE.BoxGeometry(0.1, 0.12, 0.012), cloth('#4a4640'), b).position.set(0.08, 1.05, 0.2);
  M(new THREE.BoxGeometry(0.4, 0.05, 0.3), leather('#2e241c'), b).position.set(0, 0.95, 0);
  // White hair gone wild, a long white beard.
  const white = std('#d9d6d0', { roughness: 1 });
  const hair = M(new THREE.SphereGeometry(0.14, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), white, h);
  hair.position.set(0, 1.655, -0.02);
  hair.scale.set(1.08, 1.0, 1.12);
  const beard = M(new THREE.SphereGeometry(0.11, 10, 7, 0, Math.PI * 2, Math.PI * 0.45, Math.PI * 0.55), white, h);
  beard.position.set(0, 1.575, 0.04);
  beard.scale.set(1.0, 1.45, 0.9);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.055, 0.016, 0.02), white, h).position.set(k * 0.045, 1.686, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.022, 0.055, 5), std('#c08e78', { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.135);
  nose.rotation.x = Math.PI / 2;
  if (banner) {
    // Chapter 5: the banner on its pole, unfurled over his shoulder: grey, rent, the Company's red cross whole on it.
    const pole = G(f.lhAbs, -0.28, 0.9, 0.12);
    limb(pole, V(0, -0.9, 0), V(0, 1.55, 0), 0.02, 0.022, DWOOD, 5);
    const flag = G(pole, 0, 1.3, 0);
    const grey = std('#6b7078', { roughness: 1, side: THREE.DoubleSide });
    const red = std(COMPANY_RED, { roughness: 1, side: THREE.DoubleSide });
    const sheet = M(new THREE.PlaneGeometry(0.62, 0.46, 4, 2), grey, flag);
    sheet.position.set(0.32, 0, 0);
    // Rent: the fly end cut ragged (its last column of vertices pulled in), and a slow wave in it.
    const pos = sheet.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      pos.setZ(i, Math.sin(x * 9) * 0.035);
      if (x > 0.3) pos.setY(i, pos.getY(i) * (0.55 + 0.45 * Math.abs(Math.sin(i * 2.7))));
    }
    sheet.geometry.computeVertexNormals();
    M(new THREE.PlaneGeometry(0.09, 0.44), red, flag).position.set(0.24, 0, 0.012);
    M(new THREE.PlaneGeometry(0.58, 0.08), red, flag).position.set(0.31, 0.07, 0.014);
    M(new THREE.PlaneGeometry(0.09, 0.44), red, flag).position.set(0.24, 0, -0.012);
    M(new THREE.PlaneGeometry(0.58, 0.08), red, flag).position.set(0.31, 0.07, -0.014);
  }
  // The banner, folded small and tied on his back: grey cloth, the red of the cross at the fold, cords across his chest.
  const back = G(b, 0, 1.18, -0.22);
  back.visible = !banner;
  M(new THREE.BoxGeometry(0.3, 0.36, 0.09), cloth('#6b7078'), back);
  M(new THREE.BoxGeometry(0.07, 0.36, 0.1), cloth(COMPANY_RED), back).position.set(0.05, 0, 0.005);
  M(new THREE.BoxGeometry(0.3, 0.06, 0.1), cloth(COMPANY_RED), back).position.set(0, 0.08, 0.006);
  const cord = std('#4a3a2c', { roughness: 1 });
  for (const k of [-1, 1]) limb(b, V(k * 0.14, 1.36, -0.18), V(-k * 0.12, 1.0, 0.2), 0.01, 0.01, cord, 4);
  // A goatherd's crook.
  const crook = G(f.rhAbs, 0.28, 0.9, 0.14);
  limb(crook, V(0, -0.86, 0.02), V(0, 0.55, 0), 0.016, 0.018, DWOOD, 5);
  const top = M(new THREE.TorusGeometry(0.07, 0.014, 4, 10, Math.PI * 1.2), DWOOD, crook);
  top.position.set(0.06, 0.58, 0);
  f.rig.head.rotation.x = 0.28;
  f.rig.body.rotation.x = 0.12;
  return done(c, 1.82, () => undefined, f.rig);
};

/** Fermín and Juana in the ice: two of the Company sitting back to back, frosted, their shields on their knees. */
const frozenPair = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 833);
  const frost = std('#cfe0ee', { roughness: 0.4, metalness: 0.05, flatShading: true });
  for (const [m, rot] of [
    [fermin(), 0],
    [juana(), Math.PI],
  ] as const) {
    const fig = m.root;
    fig.position.set(0, 0, rot ? 0.24 : -0.24);
    fig.rotation.y = rot;
    const rig = m.rig;
    if (rig) {
      for (const leg of [rig.legL, rig.legR]) leg.rotation.x = -1.45;
      for (const shin of [rig.shinL, rig.shinR]) if (shin) shin.rotation.x = 1.35;
      rig.armL.rotation.set(-0.9, 0, 0.25);
      rig.armR.rotation.set(-0.9, 0, -0.25);
      rig.head.rotation.x = 0.45;
    }
    fig.position.y = -0.48;
    // Frost over them: the cold's grey-blue over every surface.
    fig.traverse((x) => {
      const mesh = x as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat.color) return;
      const t = mat.clone();
      t.color = mat.color.clone().lerp(new THREE.Color('#b9cfe2'), 0.55);
      mesh.material = t;
    });
    c.g.add(fig);
  }
  // Rime heaped round them, and ice up their backs where they lean.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const r = M(new THREE.DodecahedronGeometry(0.16 + (i % 3) * 0.04, 0), frost, c.g);
    r.position.set(Math.cos(a) * 0.42, 0.05, Math.sin(a) * 0.5);
  }
  M(new THREE.BoxGeometry(0.34, 0.6, 0.12), std('#a9c6dc', { roughness: 0.25, transparent: true, opacity: 0.7 }), c.g).position.set(0, 0.35, 0);
  return done(c, 1.3, () => undefined);
};


/** The farmwoman who speaks for the farms: hooded, a heavy coat, her hair in a braid down the back. */
const farmwife = (o?: BaseOpts): Model => {
  const m = farmFolk(o, 818, { coat: '#4a4e58', under: '#5e544a', skin: '#c9a08a', hair: '#4a3a2c', hat: 'hood', build: 0.94, beard: false });
  const b = m.root.getObjectByName('model') ?? m.root;
  const braid = M(new THREE.CylinderGeometry(0.03, 0.018, 0.42, 6), std('#4a3a2c', { roughness: 1 }), b);
  braid.position.set(0, 1.36, -0.15);
  braid.rotation.x = 0.2;
  return m;
};

/** A torch in the right hand (the farms up at the line): a stick, rag wound at its head, the flame self-lit. */
const withTorch = (m: Model): Model => {
  const hand = m.root.getObjectByName('weapon');
  if (!hand) return m;
  const t = G(hand, 0, 0, 0.04);
  t.rotation.x = 0.2;
  limb(t, V(0, -0.3, 0), V(0, 0.42, 0), 0.016, 0.02, DWOOD, 5);
  M(new THREE.CylinderGeometry(0.035, 0.03, 0.08, 6), std('#4a3e30', { roughness: 1 }), t).position.y = 0.44;
  const flame = M(new THREE.ConeGeometry(0.06, 0.2, 6), new THREE.MeshBasicMaterial({ color: '#ff8a4a' }), t);
  flame.position.y = 0.56;
  const core = M(new THREE.ConeGeometry(0.03, 0.12, 6), new THREE.MeshBasicMaterial({ color: '#ffd8a8' }), t);
  core.position.y = 0.53;
  return m;
};

/** Ruy as the camp found him: sat on his stone sixty years, frosted, his shield across his knees. */
const ruyFrozen = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 834);
  const m = ruy();
  const rig = m.rig;
  if (rig) {
    for (const leg of [rig.legL, rig.legR]) leg.rotation.x = -1.45;
    for (const shin of [rig.shinL, rig.shinR]) if (shin) shin.rotation.x = 1.4;
    rig.armL.rotation.set(-0.75, 0, 0.2);
    rig.armR.rotation.set(-0.75, 0, -0.2);
    rig.head.rotation.x = 0.5;
  }
  m.root.position.y = 0.0;
  m.root.traverse((x) => {
    const mesh = x as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mat = mesh.material as THREE.MeshStandardMaterial;
    if (!mat.color) return;
    const t = mat.clone();
    t.color = mat.color.clone().lerp(new THREE.Color('#c3d5e6'), 0.5);
    mesh.material = t;
  });
  c.g.add(m.root);
  // The stone under him, rime on it; his shield on his knees.
  const rock = M(new THREE.DodecahedronGeometry(0.34, 0), std('#4d5563', { roughness: 1, flatShading: true }), c.g);
  rock.position.set(0, 0.2, -0.12);
  rock.scale.set(1.1, 0.7, 1);
  const sh = companyShield(c.g, 0.95);
  sh.position.set(0, 0.62, 0.32);
  sh.rotation.x = -1.1;
  return done(c, 1.4, () => undefined);
};

/**
 * The paladin's path (docs/sendas/paladin.md): Ser Marit, nearly sixty, grey-haired and lame of the left leg. Armour
 * worn grey with use, plates dented and mended with darker steel, a long surcoat of undyed wool gone pale as salt over
 * it, the first Vigil's open eye stitched small and faded on the breast in red; a heater shield on the left arm, its
 * paint worn to the wood at the edges, a sword in the right hand. Her grey hair cropped at the jaw, a short braid at
 * the nape; a lined, thin face. She stands a little off her left leg (the hip lower, the knee bent).
 *
 * Colors: cold steel and pale wool, the Vigil's red (#b8322a) only in the eye; no gold.
 */
const marit = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 841);
  const plate = metal('#7d838c', { roughness: 0.55 });
  const plateD = metal('#5a6069', { roughness: 0.6 });
  const wool = cloth('#cfcfc9');
  const skin = std('#cfab98', { roughness: 0.85 });
  const f = figure({ legs: plateD, boots: leather('#2a2420'), torso: plate, shoulders: plate, arms: plateD, forearms: plate, hands: leather('#3a3028'), skin, cape: '#3c424c', capeLen: 0.95 });
  c.g.add(f.fig);
  f.fig.scale.set(0.97, 0.99, 0.97);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The surcoat: long, pale wool, split at the front, its hem gone grey with the road.
  const coat = std('#d2d3cd', { roughness: 1, side: THREE.DoubleSide });
  M(new THREE.CylinderGeometry(0.205, 0.27, 0.62, 12, 1, true, 0.35, Math.PI * 2 - 0.7), coat, b).position.y = 0.86;
  M(new THREE.CylinderGeometry(0.272, 0.275, 0.05, 12, 1, true, 0.35, Math.PI * 2 - 0.7), std('#8e9097', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.57;
  M(new THREE.CylinderGeometry(0.215, 0.205, 0.42, 12, 1, true), wool, b).position.y = 1.22;
  // The open eye on the breast, small and faded: an almond and its red iris.
  const eye = G(b, 0, 1.27, 0.214);
  const almond = M(new THREE.TorusGeometry(0.045, 0.008, 4, 12), std('#5a5c62', { roughness: 1 }), eye);
  almond.scale.set(1, 0.55, 1);
  M(new THREE.CircleGeometry(0.018, 10), cloth('#b8322a'), eye).position.z = 0.002;
  // Her belt and the sword's scabbard on the left hip.
  ring(b, 0.205, 0.022, leather('#2f2620'), 1.0, 5, 14);
  const scab = M(new THREE.BoxGeometry(0.05, 0.5, 0.03), leather('#2a221d'), b);
  scab.position.set(-0.2, 0.78, 0.08);
  scab.rotation.z = 0.25;
  // Pauldrons, one of them mended (a darker plate riveted over).
  for (const k of [-1, 1]) {
    const p = M(new THREE.SphereGeometry(0.115, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.5), k < 0 ? plateD : plate, b);
    p.position.set(k * 0.235, 1.43, 0);
    p.scale.set(1.15, 0.7, 1.15);
    p.rotation.z = -k * 0.35;
  }
  M(new THREE.BoxGeometry(0.06, 0.05, 0.02), std('#3e434a', { metalness: 0.5, roughness: 0.5 }), b).position.set(-0.25, 1.44, 0.1);
  // Her head: grey hair cropped at the jaw, a short braid at the nape; a lined thin face.
  const grey = std('#b9b8b4', { roughness: 1 });
  const hair = M(new THREE.SphereGeometry(0.142, 14, 9, 0, Math.PI * 2, 0, Math.PI * 0.62), grey, h);
  hair.position.set(0, 1.648, -0.018);
  hair.scale.set(1.06, 1.0, 1.1);
  for (const k of [-1, 1]) {
    const side = M(new THREE.BoxGeometry(0.03, 0.14, 0.13), grey, h);
    side.position.set(k * 0.128, 1.585, -0.01);
  }
  for (let i = 0; i < 3; i++) M(new THREE.SphereGeometry(0.03 - i * 0.004, 6, 5), grey, h).position.set(0, 1.55 - i * 0.055, -0.14 - i * 0.01);
  const lined = std('#a07e6e', { roughness: 0.9 });
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#1a1a1e'), h).position.set(k * 0.043, 1.654, 0.115);
    M(new THREE.BoxGeometry(0.046, 0.01, 0.015), grey, h).position.set(k * 0.044, 1.68, 0.11);
    const fold = M(new THREE.BoxGeometry(0.006, 0.05, 0.01), lined, h);
    fold.position.set(k * 0.035, 1.598, 0.117);
    fold.rotation.z = k * -0.3;
  }
  const nose = M(new THREE.ConeGeometry(0.019, 0.055, 5), std('#c39f8c', { roughness: 0.85 }), h);
  nose.position.set(0, 1.628, 0.133);
  nose.rotation.x = Math.PI / 2 + 0.2;
  M(new THREE.BoxGeometry(0.044, 0.006, 0.01), std('#7a5650'), h).position.set(0, 1.58, 0.112);
  // The shield on her left arm, its paint worn back to the wood at the edges, a dent near the top.
  const shield = G(f.lhAbs, -0.3, 1.0, 0.18);
  const face = heaterShield(0.36, 0.48, std('#c7c9cc', { roughness: 0.85, flatShading: true }));
  shield.add(face);
  const rimS = heaterShield(0.4, 0.52, std('#6a5a4a', { roughness: 1, flatShading: true }));
  rimS.position.z = -0.012;
  rimS.scale.z = 0.6;
  shield.add(rimS);
  shield.rotation.y = -0.25;
  M(new THREE.SphereGeometry(0.03, 6, 4), std('#9da0a6', { roughness: 0.7 }), shield).position.set(0.08, 0.14, 0.02);
  // The sword in the right hand.
  const sw = sword(0.62, STEEL, leather('#2a221d'));
  const grip = G(f.rhAbs, 0.3, 0.92, 0.1);
  grip.add(sw);
  sw.rotation.x = -0.35;
  // Lame of the left leg: she stands off it, the left knee bent, the hip a little lower.
  f.rig.legL.rotation.x = -0.12;
  f.rig.body.rotation.z = 0.035;
  return done(c, 1.62, () => undefined, f.rig);
};

/**
 * The paladin's chapter 2 (docs/sendas/paladin.md, «Las salinas»): knights and squires of the Order's house over the
 * sea, in the house's steel and its night-blue cloak (never gold), the Vigil's eye small on the breast. `L` sets who:
 * Ser Varo (thirty-odd, dark hair cropped, a short beard, bareheaded, cloak to the calf, sword and a shield slung), one
 * of his knights (a kettle helm, the shield on his arm), Nuño (twenty-two, a squire's quilted coat under a breastplate,
 * no cloak, an arming sword), and the young squire in the fog (Marit at nineteen: the squire's armour and the Way's
 * pale cloak, a long dark braid, all of her washed pale by the fog).
 */
interface KnightLook {
  plate: string;
  under: string;
  cape: string | null;
  skin: string;
  hair: string;
  beard: boolean;
  helm: boolean;
  braid: boolean;
  build: number;
  tall: number;
  /** The shield on the arm (else slung on the back, or none). */
  shield: 'arm' | 'back' | 'none';
  /** The squire's quilted coat under a breastplate (no full plate). */
  squire: boolean;
  /** A flanged mace in the hand instead of the sword (Ser Edda). */
  mace?: boolean;
  /** The face laughing with the eyes: brows up, the lines at their corners, a small smile (Ser Edda as she was). */
  laugh?: boolean;
  /** The Vigil's eye large on the breast and on the shield (a knight of the first Vigil, not of the house). */
  vigil?: boolean;
}

const knight = (o: BaseOpts | undefined, seed: number, L: KnightLook): Model => {
  const c = begin(o, 0.62, seed);
  const plate = metal(L.plate, { roughness: 0.5 });
  const plateD = metal(L.squire ? '#4b4e54' : L.plate, { roughness: 0.62 });
  const under = cloth(L.under);
  const skin = std(L.skin, { roughness: 0.85 });
  const f = figure({ legs: L.squire ? under : plateD, boots: leather('#221d1a'), torso: L.squire ? under : plate, shoulders: L.squire ? under : plate, arms: L.squire ? under : plateD, forearms: L.squire ? leather('#3a3028') : plate, hands: leather('#2e2620'), skin, ...(L.cape ? { cape: L.cape, capeLen: 1.0 } : {}) });
  c.g.add(f.fig);
  f.fig.scale.set(L.build, L.tall, L.build);
  const b = f.bodyAbs;
  const h = f.headAbs;
  if (L.squire) {
    // The quilted coat to mid-thigh, its rows stitched; a plain breastplate over it.
    M(new THREE.CylinderGeometry(0.21, 0.26, 0.5, 12, 1, true), std(L.under, { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.92;
    for (let i = 0; i < 4; i++) ring(b, 0.215 + i * 0.01, 0.006, std('#3e3d38', { roughness: 1 }), 0.8 + i * 0.1, 4, 14);
    const bp = M(new THREE.SphereGeometry(0.2, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), plate, b);
    bp.position.set(0, 1.18, 0.02);
    bp.scale.set(1.05, 1.15, 0.85);
    bp.rotation.x = Math.PI;
  } else {
    // A white surcoat over the plate, split at the front, its hem in the house's blue.
    M(new THREE.CylinderGeometry(0.205, 0.265, 0.58, 12, 1, true, 0.35, Math.PI * 2 - 0.7), std('#dfe1e4', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.88;
    M(new THREE.CylinderGeometry(0.268, 0.272, 0.05, 12, 1, true, 0.35, Math.PI * 2 - 0.7), std('#2a3448', { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.6;
    M(new THREE.CylinderGeometry(0.212, 0.205, 0.42, 12, 1, true), std('#dfe1e4', { roughness: 1 }), b).position.y = 1.22;
    // The Vigil's eye on the breast, small: the almond and its red.
    const eye = G(b, 0, 1.27, 0.21);
    const big = L.vigil ? 1.9 : 1;
    const almond = M(new THREE.TorusGeometry(0.045 * big, 0.008 * big, 4, 12), std('#2a3448', { roughness: 1 }), eye);
    almond.scale.set(1, 0.55, 1);
    M(new THREE.CircleGeometry(0.018 * big, 10), cloth('#b8322a'), eye).position.z = 0.002;
    for (const k of [-1, 1]) {
      const pd = M(new THREE.SphereGeometry(0.115, 10, 6, 0, Math.PI * 2, 0, Math.PI * 0.5), plate, b);
      pd.position.set(k * 0.235, 1.43, 0);
      pd.scale.set(1.15, 0.7, 1.15);
      pd.rotation.z = -k * 0.35;
    }
  }
  ring(b, 0.205, 0.022, leather('#2a221d'), 1.0, 5, 14);
  const scab = M(new THREE.BoxGeometry(0.05, 0.5, 0.03), leather('#221c18'), b);
  scab.position.set(-0.2, 0.78, 0.08);
  scab.rotation.z = 0.25;
  // The head.
  const hair = std(L.hair, { roughness: 1 });
  if (L.helm) {
    const helm = M(new THREE.SphereGeometry(0.15, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.55), plate, h);
    helm.position.set(0, 1.66, 0);
    M(new THREE.CylinderGeometry(0.23, 0.23, 0.02, 16), plate, h).position.set(0, 1.66, 0);
  } else {
    const top = M(new THREE.SphereGeometry(0.135, 12, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), hair, h);
    top.position.set(0, 1.652, -0.012);
    top.scale.set(1.04, 1.0, 1.08);
  }
  if (L.braid) {
    for (let i = 0; i < 5; i++) M(new THREE.SphereGeometry(0.032 - i * 0.003, 6, 5), hair, h).position.set(0, 1.56 - i * 0.065, -0.145 - i * 0.012);
    for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.15, 0.12), hair, h).position.set(k * 0.125, 1.6, -0.02);
  }
  if (L.beard) {
    const beard = M(new THREE.SphereGeometry(0.098, 10, 7, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5), hair, h);
    beard.position.set(0, 1.605, 0.04);
    beard.scale.set(1.0, 0.7, 0.9);
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.012, 5, 4), std('#141214'), h).position.set(k * 0.044, 1.656, 0.114);
    M(new THREE.BoxGeometry(0.048, 0.012, 0.018), hair, h).position.set(k * 0.045, 1.684, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.019, 0.052, 5), std(L.skin, { roughness: 0.8 }), h);
  nose.position.set(0, 1.628, 0.133);
  nose.rotation.x = Math.PI / 2;
  if (L.laugh) {
    // Laughing with the eyes: the lines fanned at their corners, the cheeks up, a small closed smile.
    const line = std('#9a7464', { roughness: 0.9 });
    for (const k of [-1, 1]) {
      for (const t of [-0.5, 0, 0.5]) {
        const l = M(new THREE.BoxGeometry(0.02, 0.004, 0.006), line, h);
        l.position.set(k * 0.072, 1.656 + t * 0.012, 0.1);
        l.rotation.z = k * t * 0.9;
      }
      M(new THREE.SphereGeometry(0.022, 6, 5), std('#d8a894', { roughness: 0.85 }), h).position.set(k * 0.055, 1.615, 0.105);
    }
    const smile = M(new THREE.TorusGeometry(0.024, 0.0045, 3, 10, Math.PI * 0.8), std('#7a4a44'), h);
    smile.position.set(0, 1.592, 0.118);
    smile.rotation.z = Math.PI + Math.PI * 0.1;
  }
  // The sword (or a mace) in the right hand; the shield.
  const grip = G(f.rhAbs, 0.3, 0.92, 0.1);
  if (L.mace) {
    const mc = G(grip, 0, 0, 0);
    mc.rotation.x = -0.35;
    limb(mc, V(0, -0.08, 0), V(0, 0.52, 0), 0.016, 0.018, leather('#221c18'), 5);
    M(new THREE.SphereGeometry(0.06, 8, 6), STEEL, mc).position.y = 0.56;
    for (let i = 0; i < 6; i++) {
      const fl = M(new THREE.BoxGeometry(0.012, 0.13, 0.05), STEEL, mc);
      const a = (i / 6) * Math.PI * 2;
      fl.position.set(Math.cos(a) * 0.055, 0.56, Math.sin(a) * 0.055);
      fl.rotation.y = -a;
    }
  } else {
    const sw = sword(L.squire ? 0.52 : 0.64, STEEL, leather('#221c18'));
    grip.add(sw);
    sw.rotation.x = -0.35;
  }
  if (L.shield !== 'none') {
    const face = heaterShield(0.34, 0.46, std('#dfe1e4', { roughness: 0.8, flatShading: true }));
    const sh = L.shield === 'arm' ? G(f.lhAbs, -0.3, 1.0, 0.18) : G(b, 0, 1.15, -0.2);
    sh.add(face);
    const rim = heaterShield(0.38, 0.5, std('#2a3448', { roughness: 0.9, flatShading: true }));
    rim.position.z = -0.012;
    rim.scale.z = 0.6;
    sh.add(rim);
    // The house's mark: a red eye on the white (the first Vigil's, larger, with its iris).
    const em = M(new THREE.TorusGeometry(L.vigil ? 0.1 : 0.07, 0.014, 4, 12), cloth('#b8322a'), sh);
    em.scale.set(1, 0.55, 1);
    em.position.set(0, 0.02, 0.03);
    if (L.vigil) M(new THREE.CircleGeometry(0.03, 10), cloth('#b8322a'), sh).position.set(0, 0.02, 0.032);
    if (L.shield === 'arm') sh.rotation.y = -0.25;
    else sh.rotation.y = Math.PI;
  }
  return done(c, 1.66 * L.tall, () => undefined, f.rig);
};

const varo = (o?: BaseOpts): Model => knight(o, 851, { plate: '#9aa1aa', under: '#3a3e46', cape: '#25304a', skin: '#c79c84', hair: '#231d19', beard: true, helm: false, braid: false, build: 1.04, tall: 1.03, shield: 'back', squire: false });
const orderKnight = (o?: BaseOpts): Model => knight(o, 852, { plate: '#8e959e', under: '#3a3e46', cape: '#25304a', skin: '#bf9178', hair: '#4a3c30', beard: true, helm: true, braid: false, build: 1.08, tall: 1.0, shield: 'arm', squire: false });
const nuno = (o?: BaseOpts): Model => knight(o, 853, { plate: '#a7adb5', under: '#5a5850', cape: null, skin: '#d2a88e', hair: '#6a4a30', beard: false, helm: false, braid: false, build: 0.94, tall: 0.98, shield: 'none', squire: true });

/** The young squire in the fog: Marit at nineteen, the squire's armour and the Way's pale cloak, washed pale by the fog. */
const fled = (o?: BaseOpts): Model => {
  const m = knight(o, 854, { plate: '#b8bdc4', under: '#9fa3a8', cape: '#d7dade', skin: '#d9cfc9', hair: '#5a4c44', beard: false, helm: false, braid: true, build: 0.9, tall: 0.97, shield: 'none', squire: true });
  // Pale all through: the fog's light on her (no lights; the colours lifted toward white).
  m.root.traverse((x) => {
    const mesh = x as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mesh.isMesh || !mat || !('color' in mat)) return;
    const pale = mat.clone();
    pale.color = mat.color.clone().lerp(new THREE.Color('#e6eaee'), 0.45);
    pale.emissive = new THREE.Color('#6d7682');
    mesh.material = pale;
  });
  return m;
};

/**
 * The paladin's chapter 5 (docs/sendas/paladin.md §5): La Que Huyó, Marit's own nightmare, the young squire she was in
 * white, grown tall as a boss: Marit's face and braid under the Way's pale cloak, a plain shield on her arm, all of her
 * washed toward white and lit a little from within (no light), a cold breath round her edge (a glow, baked). Her
 * squires: the same girl small, without the shield.
 */
const paleOf = (m: Model, toward: string, k: number, glow: string): Model => {
  m.root.traverse((x) => {
    const mesh = x as THREE.Mesh;
    const mat = mesh.material as THREE.MeshStandardMaterial | undefined;
    if (!mesh.isMesh || !mat || !('color' in mat)) return;
    const pale = mat.clone();
    pale.color = mat.color.clone().lerp(new THREE.Color(toward), k);
    pale.emissive = new THREE.Color(glow);
    mesh.material = pale;
  });
  return m;
};
const fledBoss = (o?: BaseOpts): Model => {
  const m = paleOf(knight(o, 864, { plate: '#cdd2d8', under: '#c2c7cd', cape: '#eef1f4', skin: '#e2d8d2', hair: '#4a3e38', beard: false, helm: false, braid: true, build: 0.92, tall: 1.0, shield: 'arm', squire: true }), '#eef2f6', 0.5, '#7a8494');
  // The cold round her edge: a soft glow about her.
  glowSprite(m.root, '#dfe8ff', 1.9, V(0, 0.95, 0), 0.16);
  return m;
};
const fledSquire = (o?: BaseOpts): Model => {
  const m = paleOf(knight(o, 865, { plate: '#c4c9d0', under: '#b7bcc2', cape: '#e4e7ea', skin: '#ddd3cd', hair: '#4a3e38', beard: false, helm: false, braid: true, build: 0.86, tall: 0.92, shield: 'none', squire: true }), '#e8ecf0', 0.45, '#6a7482');
  glowSprite(m.root, '#dfe8ff', 1.1, V(0, 0.9, 0), 0.18);
  return m;
};

/**
 * The paladin's chapter 4 (docs/sendas/paladin.md, «El monasterio de la cascada»): the memory's two, as they were years
 * before the fall, and the monastery's old monk.
 * - Marit at seventeen, the squire: the quilted coat under a plain breastplate, a long dark braid, the Way's pale cloak,
 *   a plain shield; as `fled` but alive (her own colours, nothing washed out).
 * - Ser Edda as she was: a tall knight in bright plate, the white surcoat with the first Vigil's eye large in red, a
 *   long black braid, bareheaded, laughing with the eyes; a flanged mace and the Vigil's shield. Never the Unsleeping's
 *   black glass (models/edda.ts).
 * - Brother Tadeo: old, a little stooped, a grey-brown habit to the feet with its hood down on his shoulders, a rope
 *   belt, a white fringe round his bald crown and a short white beard; sandals.
 */
const maritYoung = (o?: BaseOpts): Model =>
  knight(o, 861, { plate: '#a6acb4', under: '#545a63', cape: '#d4d7da', skin: '#dcb39c', hair: '#3b2b22', beard: false, helm: false, braid: true, build: 0.9, tall: 0.96, shield: 'arm', squire: true });
const eddaYoung = (o?: BaseOpts): Model =>
  knight(o, 862, { plate: '#b3b9c1', under: '#3a3e46', cape: '#d9dce0', skin: '#d4a990', hair: '#1c1a1d', beard: false, helm: false, braid: true, build: 1.0, tall: 1.09, shield: 'arm', squire: false, mace: true, laugh: true, vigil: true });

const tadeo = (o?: BaseOpts): Model => {
  const c = begin(o, 0.6, 863);
  const habit = '#5a554f';
  const skin = std('#c99f88', { roughness: 0.85 });
  const wool = cloth(habit);
  const f = figure({ legs: wool, boots: leather('#3a2c22'), torso: wool, shoulders: wool, arms: wool, forearms: wool, hands: skin, skin });
  c.g.add(f.fig);
  f.fig.scale.set(0.95, 0.96, 0.95);
  const b = f.bodyAbs;
  const h = f.headAbs;
  // The habit to the feet, wide sleeves; the hood fallen on the shoulders; the rope belt, its end hanging.
  M(new THREE.CylinderGeometry(0.2, 0.31, 1.0, 12, 1, true), std(habit, { roughness: 1, side: THREE.DoubleSide }), b).position.y = 0.55;
  const hood = M(new THREE.TorusGeometry(0.15, 0.07, 6, 12), std('#524d47', { roughness: 1 }), b);
  hood.position.set(0, 1.44, -0.04);
  hood.rotation.x = Math.PI / 2 + 0.35;
  M(new THREE.ConeGeometry(0.13, 0.2, 6), std('#524d47', { roughness: 1 }), b).position.set(0, 1.33, -0.18);
  const rope = std('#a29c90', { roughness: 1 });
  ring(b, 0.215, 0.016, rope, 0.98, 4, 14);
  limb(b, V(0.08, 0.97, 0.2), V(0.1, 0.62, 0.23), 0.012, 0.01, rope, 4);
  M(new THREE.SphereGeometry(0.022, 5, 4), rope, b).position.set(0.1, 0.6, 0.23);
  // Bald on top, a white fringe, a short white beard; deep lines.
  const white = std('#e2e0dc', { roughness: 1 });
  const fringe = M(new THREE.TorusGeometry(0.115, 0.03, 5, 14), white, h);
  fringe.position.set(0, 1.64, -0.015);
  fringe.rotation.x = Math.PI / 2 + 0.2;
  const beard = M(new THREE.SphereGeometry(0.09, 10, 7, 0, Math.PI * 2, Math.PI * 0.5, Math.PI * 0.5), white, h);
  beard.position.set(0, 1.6, 0.04);
  beard.scale.set(1.0, 0.75, 0.9);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.011, 5, 4), std('#141214'), h).position.set(k * 0.043, 1.655, 0.114);
    M(new THREE.BoxGeometry(0.046, 0.012, 0.016), white, h).position.set(k * 0.044, 1.68, 0.11);
  }
  const nose = M(new THREE.ConeGeometry(0.021, 0.058, 5), std('#c0957e', { roughness: 0.85 }), h);
  nose.position.set(0, 1.628, 0.134);
  nose.rotation.x = Math.PI / 2 + 0.2;
  // Stooped: the back bent a little, the head lifted to look ahead.
  f.rig.body.rotation.x = 0.14;
  f.rig.head.rotation.x = -0.12;
  return done(c, 1.55, () => undefined, f.rig);
};

/**
 * The salt folk (four houses on the white coast): coats gone grey with salt, the crust white at the hems. The old
 * salt-woman with her wooden salt shovel; one of the salt with a sack of it; another with a rake.
 */
const withShovel = (m: Model, rake = false): Model => {
  const hand = m.root.getObjectByName('weapon');
  if (!hand) return m;
  const t = G(hand, 0, 0, 0.04);
  t.rotation.x = 0.15;
  limb(t, V(0, -0.55, 0), V(0, 0.75, 0), 0.016, 0.018, WOOD, 5);
  if (rake) {
    const head = M(new THREE.BoxGeometry(0.34, 0.03, 0.04), DWOOD, t);
    head.position.y = 0.76;
    for (let i = 0; i < 6; i++) M(new THREE.BoxGeometry(0.012, 0.07, 0.012), DWOOD, t).position.set(-0.15 + i * 0.06, 0.71, 0.02);
  } else {
    const blade = M(new THREE.BoxGeometry(0.2, 0.26, 0.02), std('#9a8a72', { roughness: 1 }), t);
    blade.position.y = -0.62;
  }
  // Salt crusted on it.
  M(new THREE.BoxGeometry(0.16, 0.05, 0.03), std('#f2f4f6', { roughness: 0.9 }), t).position.y = rake ? 0.73 : -0.72;
  return m;
};
const saltCrust = (m: Model): Model => {
  const b = m.root.getObjectByName('model') ?? m.root;
  ring(b as THREE.Object3D, 0.3, 0.025, std('#eef1f3', { roughness: 0.95 }), 0.53, 4, 14);
  return m;
};
const salinera = (o?: BaseOpts): Model => withShovel(saltCrust(farmFolk(o, 855, { coat: '#7d7f7c', under: '#5d5e5a', skin: '#c49c86', hair: '#d0cec8', hat: 'hood', build: 0.9, beard: false })));
const salter = (o?: BaseOpts): Model => saltCrust(farmFolk(o, 856, { coat: '#6a6c69', under: '#4f504c', skin: '#b98d72', hair: '#3a3029', hat: 'brim', build: 1.02, beard: true, sack: true }));
const salter2 = (o?: BaseOpts): Model => withShovel(saltCrust(farmFolk(o, 857, { coat: '#73706b', under: '#5a5650', skin: '#c79f88', hair: '#2e2620', hat: 'hood', build: 0.94, beard: false })), true);

/**
 * The Carcoma (docs/sendas/druida.md §5): the rot of the Grandsire's wood grown into a beast, the size of a house.
 * Read from the game's camera, from above: a hunched shell of rotten bark split into plates, the pale sick flesh of the
 * rot glowing violet in every crack between them; broken shards of bark standing up off its back like a crown of
 * splinters; shelf fungi down its flanks; at its front a maw ringed with long splinters of wood, a violet glow down
 * its throat, a few pale eyes over it. Black roots trail off it into the earth (the four great ones to the walls are
 * the chamber's, engine/raices.ts). It breathes slowly; its maw works.
 * (Cold rotten browns and violets: under the chamber's amber anything paler came out cream or pink.)
 */
const carcoma = (o?: BaseOpts): Model => {
  const c = begin(o, 1.2, 905);
  const g = c.g;
  const barkA = std('#3a2c26', { roughness: 0.9, flatShading: true });
  const barkB = std('#2a201c', { roughness: 0.9, flatShading: true });
  const barkC = std('#4a3a34', { roughness: 0.85, flatShading: true });
  const rotten = std('#3e3048', { roughness: 0.8, flatShading: true });
  const flesh = emis('#5a5068', '#5a3c8a', 0.35, { roughness: 0.55, flatShading: true });
  const crack = emis('#c8a8ff', '#a478ff', 1.6, { roughness: 0.4 });
  const splinter = std('#a89c90', { roughness: 0.8, flatShading: true });
  const splinterDark = std('#6e625a', { roughness: 0.85, flatShading: true });
  const fungus = std('#8c7e9a', { roughness: 0.7, flatShading: true });
  const fungusRim = std('#d8d0e4', { roughness: 0.6 });
  const root = std('#120e12', { roughness: 0.85, flatShading: true });
  const throatM = std('#0a0408', { roughness: 0.9 });
  const body = G(g);
  // The rot's flesh under it all: a squat lumpy mass, glowing faintly.
  for (const [x, y, z, sx, sy, sz] of [
    [0, 0.46, -0.05, 0.78, 0.5, 0.74],
    [0.32, 0.34, 0.25, 0.42, 0.32, 0.42],
    [-0.34, 0.34, 0.22, 0.42, 0.32, 0.42],
  ] as const) {
    const m = M(new THREE.DodecahedronGeometry(1, 0), flesh, body);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
  }
  // The shell of bark plates over its back, split apart: three rings of plates, each tilted out from the middle,
  // gaps between them where the flesh shows.
  const plates: [number, number, number, number][] = [
    // [ring's height, its radius, plates in it, the plates' size]
    [0.92, 0.18, 3, 0.26],
    [0.78, 0.48, 7, 0.3],
    [0.5, 0.74, 10, 0.28],
  ];
  plates.forEach(([y, r, n, sz], ri) => {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + ri * 0.35;
      // (The front, where the maw is, left open.)
      if (ri === 2 && Math.abs(Math.atan2(Math.sin(a), Math.cos(a)) - Math.PI / 2) < 0.5) continue;
      const pl = M(new THREE.BoxGeometry(sz, 0.07, sz * 1.25), [barkA, barkB, barkC][(i + ri) % 3] as THREE.Material, body);
      pl.position.set(Math.cos(a) * r, y - (i % 2) * 0.03, Math.sin(a) * r * 0.92);
      pl.rotation.set(0, -a + Math.PI / 2, 0);
      pl.rotateX(ri === 0 ? 0.2 : 0.35 + ri * 0.22);
      pl.rotateZ(((i * 7) % 5) * 0.06 - 0.12);
      // Ridges of bark on each plate.
      for (let k = -1; k <= 1; k++) {
        const rg = M(new THREE.BoxGeometry(0.025, 0.03, sz * 1.15), barkB, pl);
        rg.position.set(k * sz * 0.28, 0.045, 0);
      }
    }
  });
  // The cracks between the plates: glowing seams of the rot, running down from the crown.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.2;
    if (Math.abs(Math.atan2(Math.sin(a), Math.cos(a)) - Math.PI / 2) < 0.35) continue;
    limb(body, V(Math.cos(a) * 0.12, 1.0, Math.sin(a) * 0.12), V(Math.cos(a) * 0.62, 0.66, Math.sin(a) * 0.58), 0.03, 0.025, crack, 4);
    limb(body, V(Math.cos(a) * 0.62, 0.66, Math.sin(a) * 0.58), V(Math.cos(a + 0.12) * 0.84, 0.3, Math.sin(a + 0.12) * 0.78), 0.025, 0.015, crack, 4);
  }
  // Broken shards of bark standing up off its back, jagged, leaning out: its crown of splinters.
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2 + 0.6;
    const r = 0.22 + (i % 3) * 0.16;
    const h = 0.32 + ((i * 5) % 4) * 0.09;
    const sh = M(new THREE.ConeGeometry(0.06 + (i % 2) * 0.03, h, 4), i % 3 ? barkB : splinterDark, body);
    sh.position.set(Math.cos(a) * r, 0.98 + h * 0.4 - r * 0.25, Math.sin(a) * r * 0.9 - 0.06);
    sh.rotation.set(Math.sin(a) * 0.5, a, -Math.cos(a) * 0.5);
  }
  // Shelf fungi down its flanks, pale violet with a bright rim.
  for (let i = 0; i < 6; i++) {
    const a = Math.PI * (0.95 + (i / 5) * 1.1);
    const f = M(new THREE.CylinderGeometry(0.15 - (i % 2) * 0.04, 0.17 - (i % 2) * 0.04, 0.04, 10, 1, false, 0, Math.PI), fungus, body);
    f.position.set(Math.cos(a) * 0.8, 0.32 + (i % 3) * 0.1, Math.sin(a) * 0.74);
    f.rotation.y = -a - Math.PI / 2;
    const rim = M(new THREE.TorusGeometry(0.16 - (i % 2) * 0.04, 0.012, 4, 10, Math.PI), fungusRim, f);
    rim.rotation.x = Math.PI / 2;
  }
  // The maw at its front: a dark hole, long splinters of wood round it pointing in (upper and lower jaw), the rot's
  // glow down its throat; pale eyes over it.
  const face = G(body, 0, 0.42, 0.66);
  const jawU = G(face);
  const jawL = G(face);
  const hole = M(new THREE.SphereGeometry(0.26, 12, 8), throatM, face);
  hole.scale.set(1.25, 0.8, 0.5);
  glowSprite(face, '#b48cff', 0.55, V(0, 0, 0.02), 0.45);
  for (let i = 0; i < 14; i++) {
    const up = i < 7;
    const k = (i % 7) / 6 - 0.5;
    const len = 0.22 + (1 - Math.abs(k) * 1.6) * 0.14 + ((i * 3) % 4) * 0.02;
    const sp = M(new THREE.ConeGeometry(0.03, len, 4), i % 3 ? splinter : splinterDark, up ? jawU : jawL);
    const ax = k * 0.62;
    sp.position.set(ax, up ? 0.2 - Math.abs(k) * 0.12 : -0.2 + Math.abs(k) * 0.12, 0.06);
    // Pointing into the hole and out, as a broken log's splinters.
    sp.rotation.set(up ? -2.2 : -0.9, 0, -k * (up ? -1 : 1) * 0.9);
  }
  // The lip of rotten bark round the maw.
  const lip = M(new THREE.TorusGeometry(0.3, 0.07, 5, 14), rotten, face);
  lip.scale.set(1.25, 0.82, 0.8);
  for (const [x, y] of [
    [-0.2, 0.34],
    [0.2, 0.34],
    [-0.08, 0.4],
    [0.09, 0.42],
  ] as const) {
    const eye = M(new THREE.SphereGeometry(0.035, 7, 5), emis('#e8e0ff', '#c8b0ff', 1.5), face);
    eye.position.set(x, y, 0.02);
  }
  // Black roots trailing off it into the earth, thick at the body.
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2 + 0.25;
    if (Math.abs(Math.atan2(Math.sin(a), Math.cos(a)) - Math.PI / 2) < 0.4) continue;
    const p0 = V(Math.cos(a) * 0.7, 0.2, Math.sin(a) * 0.66);
    const p1 = V(Math.cos(a + 0.2) * 1.1, 0.06, Math.sin(a + 0.2) * 1.05);
    const p2 = V(Math.cos(a + 0.35) * 1.45, 0.0, Math.sin(a + 0.35) * 1.4);
    limb(g, p0, p1, 0.11, 0.07, root, 5);
    limb(g, p1, p2, 0.07, 0.02, root, 5);
  }
  const tick = (n: number): void => {
    body.scale.set(1 + Math.sin(n * 0.5) * 0.04, 1 - Math.sin(n * 0.5) * 0.05, 1 + Math.cos(n * 0.45) * 0.03);
    const open = 0.5 + 0.5 * Math.sin(n * 0.9);
    jawU.position.y = 0.04 * open;
    jawL.position.y = -0.05 * open;
    jawU.rotation.x = -0.15 * open;
    jawL.rotation.x = 0.2 * open;
  };
  return done(c, 1.6, tick);
};

/** The paths' own miniatures by name (packages/content `SendaCast.model`). */
export const sendaModel = (id: string, o?: BaseOpts): Model => {
  switch (id) {
    case 'kel':
      return kel(o);
    case 'wennMule':
      return wennMule(o);
    case 'shield':
      return shieldUp(o);
    case 'dalmo':
      return dalmo(o);
    case 'farmer':
      return farmer(o);
    case 'farmer2':
      return farmer2(o);
    case 'ruy':
      return ruy(o);
    case 'fermin':
      return fermin(o);
    case 'juana':
      return juana(o);
    case 'sanne':
      return sanne(o);
    case 'tome':
      return tome(o);
    case 'harl':
      return harl(o);
    case 'muleLoad':
      return muleLoad(o);
    case 'harlOld':
      return harlOld(o);
    case 'frozenPair':
      return frozenPair(o);
    case 'harlBanner':
      return harlOld(o, true);
    case 'farmwife':
      return farmwife(o);
    case 'farmTorch':
      return withTorch(farmwife(o));
    case 'farmTorch2':
      return withTorch(farmer(o));
    case 'farmTorch3':
      return withTorch(farmFolk(o, 819, { coat: '#454039', under: '#585048', skin: '#bf927a', hair: '#2e2620', hat: 'brim', build: 1.04, beard: true }));
    case 'ruyFrozen':
      return ruyFrozen(o);
    case 'marit':
      return marit(o);
    case 'varo':
      return varo(o);
    case 'orderKnight':
      return orderKnight(o);
    case 'nuno':
      return nuno(o);
    case 'fled':
      return fled(o);
    case 'fledBoss':
      return fledBoss(o);
    case 'fledSquire':
      return fledSquire(o);
    case 'maritYoung':
      return maritYoung(o);
    case 'eddaYoung':
      return eddaYoung(o);
    case 'tadeo':
      return tadeo(o);
    case 'salinera':
      return salinera(o);
    case 'salter':
      return salter(o);
    case 'salter2':
      return salter2(o);
    case 'carcoma':
      return carcoma(o);
    default:
      // The artificer's path's people (models/taller.ts, models/corazon.ts): Grillo, Nadd, Dunna, Tormo unfinished;
      // Adela and the quarrymen, Brask and the clan; the memory of Grada (models/ojos.ts). The barbarian's path's people (models/caza.ts). The druid's (models/bosque.ts, models/otono.ts).
      // The ranger's path's people (models/aster.ts): Sela.
      return tallerModel(id, o) ?? corazonModel(id, o) ?? ojosModel(id, o) ?? tormoModel(id, o) ?? biblioModel(id, o) ?? asterModel(id, o) ?? cazaModel(id, o) ?? caminoModel(id, o) ?? pasarelaModel(id, o) ?? nieveModel(id, o) ?? ermitaModel(id, o) ?? bosqueModel(id, o) ?? otonoModel(id, o) ?? wenn(o);
  }
};
